import { and, eq, or } from 'tio-ui/utilities';
import { concat } from '@ember/helper';
import { get } from '@ember/object';
import { HStack, Section } from 'tio-ui/components/layout';
import { PhSealCheck, PhUserCheck } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import Chip from 'tio-ui/components/chip';
import DefinitionList from '../../../ui/definition-list.gts';
import formatPhone from '../../../../helpers/format-phone.ts';
import pslfApproverDisplayEmail from '../../../../helpers/pslf-approver-display-email.ts';
import safeFormatDate from '../../../../helpers/safe-format-date.ts';
import type { TOC } from '@ember/component/template-only';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';

export interface PslfAdminProfileFormDetailsSignature {
  Args: {
    model: PslfFormModel;
    isTioAdmin: boolean;
  };
  Element: HTMLElement;
}
const ProfilePslfAdminProfileFormDetails: TOC<PslfAdminProfileFormDetailsSignature> = <template>
  <Section ...attributes>
    <:header>
      <div class="tio-row-tight">
        {{t "common.pslf.forms.form_summary"}}
        {{#if @model.isOboForm}}
          <Chip @intent="primary" @size="sm" class="w-fit">
            <PhUserCheck @size="12" />
            {{t "common.pslf.forms.submitted_by_approver"}}
          </Chip>
        {{/if}}
      </div>
    </:header>
    <:body>
      <DefinitionList as |options|>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.status"}}:</options.dt>
          <options.dd>
            {{t (concat "common.pslf.status." @model.status)}}
            {{#if (or (eq @model.faxVerified "verified") (eq @model.faxVerified "human_verified"))}}
              <span class="tio-inline-row app-icon-success text-xs">
                <PhSealCheck @size="16" class="mx-1" />
                {{t "common.pslf.forms.verified"}}
              </span>
            {{/if}}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.borrower_sign_date"}}:</options.dt>
          <options.dd>
            {{#if @model.borrowerCompletedAt}}
              {{safeFormatDate
                @model.borrowerCompletedAt
                month="short"
                day="2-digit"
                year="numeric"
                hour="numeric"
                minute="numeric"
              }}
            {{/if}}
          </options.dd>
        </div>
        {{#if (and @model.isEmployerSignLinkValid @isTioAdmin)}}
          <div class={{options.wrappingClass}}>
            <options.dt>{{t "pslf.forms.employer_sign_link"}}</options.dt>
            <options.dd>{{@model.employerSignLink}}</options.dd>
          </div>
        {{/if}}
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.employer_sign_date"}}:</options.dt>
          <options.dd>
            {{#if @model.employerCompletedAt}}
              {{safeFormatDate
                @model.employerCompletedAt
                month="short"
                day="2-digit"
                year="numeric"
                hour="numeric"
                minute="numeric"
              }}
            {{/if}}
            {{#if @model.isPreApproved}}
              <span class="app-status-chip app-status-panel--info app-status-text-info ml-1">
                {{t "common.pslf.forms.auto"}}
              </span>
            {{/if}}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.fax_id"}}:</options.dt>
          <options.dd>{{@model.faxId}}</options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.submitted_at"}}</options.dt>
          <options.dd>
            {{safeFormatDate
              @model.submittedAt
              month="short"
              day="2-digit"
              year="numeric"
              hour="numeric"
              minute="numeric"
            }}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.form_type"}}</options.dt>
          <options.dd>
            {{t (concat "common.pslf.form-type." @model.formTypeDisplayKey)}}
            {{#if @model.isCurrentEmployer}}
              <span class="mx-1">-</span>{{if
                @model.isNonPartnerApproverForm
                (t "common.pslf.forms.non_partner_approver")
                (t "common.pslf.forms.tio_partner_approver")
              }}
            {{/if}}
            {{#if (and @model.isPriorEmployer @model.isPriorEmployerClosed)}}
              <span class="mx-1">-</span>{{t "common.pslf.forms.employer_closed"}}
            {{/if}}
          </options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.employer"}}:</options.dt>
          <options.dd>{{@model.employerDisplayName}}</options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.approver"}}:</options.dt>
          <options.dd>{{pslfApproverDisplayEmail @model}}</options.dd>
        </div>
        <div class={{options.wrappingClass}}>
          <options.dt>{{t "common.pslf.forms.group_id"}}</options.dt>
          <options.dd>{{@model.pslfGroup.id}}</options.dd>
        </div>
      </DefinitionList>
    </:body>
  </Section>

  <Section>
    <HStack @collapsed={{true}}>
      <Section>
        <:header>{{t "common.pslf.forms.borrower_header"}}</:header>
        <:body>
          {{#let @model.borrowerFields as |fields|}}
            <DefinitionList as |options|>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.name"}}:</options.dt>
                <options.dd>{{get fields "borrower-name"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.email"}}:</options.dt>
                <options.dd>{{get fields "borrower-email"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.date_of_birth"}}:</options.dt>
                <options.dd>{{get fields "borrower-birth-date"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.address"}}:</options.dt>
                <options.dd>
                  {{get fields "borrower-address"}}<br />
                  {{#if (get fields "borrower-apartment")}}
                    {{get fields "borrower-apartment"}}<br />
                  {{/if}}
                  {{get fields "borrower-city"}}
                  {{get fields "borrower-state"}}
                  {{get fields "borrower-zip"}}
                  {{#if (eq (get fields "borrower-address-verification-status") "valid")}}
                    <div class="tio-row-end app-icon-success text-xs">
                      <PhSealCheck @size="16" class="mx-1" />
                      {{t "common.pslf.forms.verified"}}
                    </div>
                  {{/if}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.primary_phone"}}:</options.dt>
                <options.dd>
                  {{formatPhone (get fields "borrower-primary-phone")}}
                  {{#if (get fields "borrower-primary-phone-verified")}}
                    <span class="tio-inline-row app-icon-success text-xs">
                      <PhSealCheck @size="16" class="mx-1" />
                    </span>
                  {{/if}}
                </options.dd>
              </div>
              {{#if (or @model.isPriorEmployer @model.isNonPartnerApproverForm)}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.employer_name"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-name"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.employer_email"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-email"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.employer_fein"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-ein"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.employer_website"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-website"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.contact_name"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-contact-name"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.contact_title"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employer-contact-title"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.contact_phone"}}:</options.dt>
                  <options.dd>{{formatPhone
                      (get fields "borrower-employer-contact-phone-number")
                    }}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.employment_start_date"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employment-start-date"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.employment_end_date"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employment-end-date"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.still_employed"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employment-continues"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.pslf.forms.weekly_hours"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employment-hours"}}</options.dd>
                </div>
                <div class={{options.wrappingClass}}>
                  <options.dt>{{t "common.employment_status"}}:</options.dt>
                  <options.dd>{{get fields "borrower-employment-status"}}</options.dd>
                </div>
              {{/if}}
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.date_of_signature"}}:</options.dt>
                <options.dd>
                  {{#if @model.borrowerCompletedAt}}
                    {{safeFormatDate
                      @model.borrowerCompletedAt
                      month="short"
                      day="2-digit"
                      year="numeric"
                    }}
                  {{/if}}
                </options.dd>
              </div>
            </DefinitionList>
          {{/let}}
        </:body>
      </Section>
      <Section>
        <:header>
          {{t "pslf.forms.employer_header"}}
        </:header>
        <:body>
          {{#let @model.employerFields as |fields|}}

            <DefinitionList as |options|>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.name"}}:</options.dt>
                <options.dd>{{get fields "employer-name"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.fein"}}:</options.dt>
                <options.dd>{{get fields "employer-fein"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.website"}}:</options.dt>
                <options.dd>{{get fields "employer-website"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.address"}}:</options.dt>
                <options.dd>
                  {{get fields "employer-street-address"}}<br />
                  {{#if (get fields "employer-apartment")}}
                    {{get fields "employer-apartment"}}<br />
                  {{/if}}
                  {{get fields "employer-city"}}
                  {{get fields "employer-state"}}
                  {{get fields "employer-zip"}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.contact_name"}}:</options.dt>
                <options.dd>{{get fields "employer-contact-name"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.contact_email"}}:</options.dt>
                <options.dd>{{get fields "employer-contact-email"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.contact_title"}}:</options.dt>
                <options.dd>{{get fields "employer-contact-title"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.contact_phone"}}:</options.dt>
                <options.dd>{{formatPhone
                    (get fields "employer-contact-phone-number")
                  }}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.employment_start_date"}}:</options.dt>
                <options.dd>{{get fields "employment-start-date"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.employment_end_date"}}:</options.dt>
                <options.dd>{{get fields "employment-end-date"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.still_employed"}}:</options.dt>
                <options.dd>{{get fields "employment-continues"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.weekly_hours"}}:</options.dt>
                <options.dd>{{get fields "employment-hours"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.employment_status"}}:</options.dt>
                <options.dd>{{get fields "employment-status"}}</options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>{{t "common.pslf.forms.date_of_signature"}}:</options.dt>
                <options.dd>{{get fields "employer-sign-date"}}</options.dd>
              </div>
            </DefinitionList>
          {{/let}}
        </:body>
      </Section>
    </HStack>
  </Section>
</template>;

export default ProfilePslfAdminProfileFormDetails;
